import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  EMPTY,
  exhaustMap,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { Option } from '@sapphire/result';

import { Nullable } from '@ps/utils';
import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { APP_ROUTES } from '@ps/core-config';
import { ToastService } from '@ps/core-overlay';
import {
  locationCodesClearedMessage,
  LOCATION_UPLOAD_MESSAGES,
  locationUploadRejectedMessage,
  locationUploadSuccessMessage,
  QUALIFICATION_CODE,
  SURVEY_ACTIVITY_LOG_MESSAGES,
  SurveyLocationUploadResult,
  SurveyPricingSnapshot,
} from '@ps/survey-utils';
import {
  UserSettingsActionsFacade,
  UserSettingsSelectorsFacade,
} from '@ps/user-settings-data-access';
import {
  SurveyActivityLogActions,
  SurveyService,
} from '@ps/survey-data-access';

import { SurveyForm } from '../../utils';
import { LocationUploadService } from '../../services/location/location-upload.service';
import { SurveyProcessingService } from '../../services/survey-processing/survey-processing.service';

import { selectFeasibilityCpi, selectPricing } from '../insights';

import {
  SurveyCreationActions,
  SurveyCreationApiActions,
} from './survey-creation-audience.actions';
import {
  selectLocationFileUpload,
  selectQualificationsForm,
  selectSurvey,
  selectSurveyFormState,
  selectSurveyId,
} from './survey-creation-audience.selectors';

@Injectable()
export class SurveyCreationAudienceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _toastService = inject(ToastService);
  private readonly _router = inject(Router);

  private readonly _surveyService = inject(SurveyService);
  private readonly _surveyProcessingService = inject(SurveyProcessingService);
  private readonly _locationUploadService = inject(LocationUploadService);

  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _userSettingsActions = inject(UserSettingsActionsFacade);

  public readonly createStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.createStandardSurvey),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyFormState),
        this._store.select(selectPricing),
        this._store.select(selectFeasibilityCpi),
      ]),
      map(([, surveyId, formState, pricingState, feasibilityCpi]) => ({
        surveyId,
        form: SurveyForm.from(formState),
        pricingState,
        feasibilityCpi,
      })),
      tap(({ form }) => {
        if (form.showErrors(true)) {
          this._toastError(form.error);
        }
      }),
      filter(
        ({ surveyId, form }) =>
          !surveyId && form.valid && form.isStandardSurvey(),
      ),
      map(({ form, pricingState, feasibilityCpi }) => ({
        user: this._userData(),
        pricing: {
          ...pricingState,
          feasibilityCpi,
        } satisfies SurveyPricingSnapshot,
        ...Option.from(form.data).unwrap(),
      })),
      exhaustMap(({ user, metadata, pricing }) =>
        this._surveyService
          .createStandardSurvey(metadata, user.id, user.companyId, pricing)
          .pipe(
            map((survey) => {
              this._toastService.success('Survey successfully saved', {
                keepOpen: false,
              });
              return SurveyCreationApiActions.createStandardSurveySuccess({
                survey,
              });
            }),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationApiActions.createStandardSurveyFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  // The create page hands the survey off to the edit route as soon as it exists.
  // Safe now that the edit page renders the Advanced Targeting / Set Location
  // block and hydrates a saved survey's qualifications — the reason this was
  // held back while the creation route stood alone.
  public readonly createStandardSurveyNavigate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationApiActions.createStandardSurveySuccess),
        tap(({ survey }) => {
          this._router.navigate([APP_ROUTES.editSurvey(survey.id)]);
        }),
      ),
    { dispatch: false },
  );

  public readonly saveStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.saveSurvey),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyFormState),
        this._store.select(selectPricing),
        this._store.select(selectFeasibilityCpi),
      ]),
      map(([, surveyId, formState, pricingState, feasibilityCpi]) => ({
        surveyId,
        form: SurveyForm.from(formState),
        pricingState,
        feasibilityCpi,
      })),
      tap(({ form }) => {
        if (form.showErrors()) {
          this._toastError(form.error);
        }
      }),
      filter(
        ({ surveyId, form }) =>
          !!surveyId && form.valid && form.isStandardSurvey(),
      ),
      map(({ form, surveyId, pricingState, feasibilityCpi }) => ({
        user: this._userData(),
        surveyId: Option.from(surveyId).unwrap(),
        pricing: {
          ...pricingState,
          feasibilityCpi,
        } satisfies SurveyPricingSnapshot,
        ...Option.from(form.data).unwrap(),
      })),
      switchMap(({ user, surveyId, metadata, pricing }) =>
        this._surveyService
          .updateStandardSurvey(
            surveyId,
            metadata,
            user.id,
            user.companyId,
            pricing,
          )
          .pipe(
            map((survey) => {
              this._toastService.success('Survey successfully saved', {
                keepOpen: false,
              });
              return SurveyCreationApiActions.saveSurveySuccess({
                survey,
              });
            }),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationApiActions.saveSurveyFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly updatedSurveyQualificationsValidation$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.updateQualifications),
        concatLatestFrom(() => [
          this._store.select(selectSurveyId),
          this._store.select(selectQualificationsForm),
        ]),
        filter(([, surveyId]) => !!surveyId),
        tap(([, , qualifications]) => {
          Option.from(qualifications.errors[0]?.message).inspect((message) =>
            this._toastService.error(message, { keepOpen: false }),
          );
        }),
      ),
    { dispatch: false },
  );

  public readonly loadSurveyForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.init),
      filter(
        (action): action is typeof action & { surveyId: number } =>
          !!action.surveyId,
      ),
      concatLatestFrom(() => this._store.select(selectSurvey)),
      // Skip the fetch when we already hold this exact survey in memory —
      // e.g. right after create → edit, where the create response already
      // has everything loadSurveyForEdit would otherwise re-fetch.
      filter(([{ surveyId }, survey]) => !survey || survey.id !== surveyId),
      switchMap(([{ surveyId }]) =>
        this._surveyService.getByIdTargeting(surveyId).pipe(
          map((survey) =>
            SurveyCreationApiActions.loadSurveyForEditSuccess({ survey }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationApiActions.loadSurveyForEditFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly uploadLocationCodes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.uploadLocationCodes),
      mergeMap(
        ({
          countryCode,
          surveyId,
          qualificationCode,
          qualificationName,
          file,
        }) =>
          this._locationUploadService
            .uploadLocationCodes(countryCode, surveyId, qualificationCode, file)
            .pipe(
              tap((result) =>
                this._notifyLocationUpload(
                  result,
                  qualificationCode,
                  qualificationName,
                ),
              ),
              map((result) =>
                SurveyCreationApiActions.uploadLocationCodesSuccess({
                  qualificationCode,
                  qualificationName,
                  result,
                }),
              ),
              this.psCatchError(
                (error) =>
                  of(
                    SurveyCreationApiActions.uploadLocationCodesFailure({
                      qualificationCode,
                      error:
                        error.message || LOCATION_UPLOAD_MESSAGES.UPLOAD_FAILED,
                    }),
                  ),
                { notify: true },
              ),
            ),
      ),
    ),
  );

  public readonly notifyLocationCodesCleared$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.clearLocationCodes),
        tap(({ qualificationName }) => {
          this._toastService.success(
            locationCodesClearedMessage(qualificationName),
            { keepOpen: false },
          );
        }),
      ),
    { dispatch: false },
  );

  public readonly downloadLocationCodesTemplate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.downloadLocationCodesTemplate),
        switchMap(({ countryCode, qualificationCode }) =>
          this._locationUploadService
            .downloadTemplateFile(countryCode, qualificationCode)
            .pipe(this.psCatchError(() => EMPTY, { notify: true })),
        ),
      ),
    { dispatch: false },
  );

  public readonly downloadValidatedLocationFile$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.downloadValidatedLocationFile),
        concatLatestFrom(() => this._store.select(selectLocationFileUpload)),
        map(([{ qualificationCode }, upload]) =>
          upload.qualificationCode === qualificationCode
            ? Option.from(upload.result)
            : Option.none,
        ),
        filter((upload) => upload.isSome()),
        switchMap((upload) => {
          const result = upload.unwrap();

          return this._locationUploadService
            .downloadValidatedFile(result.filePath, result.validCodes)
            .pipe(this.psCatchError(() => EMPTY, { notify: true }));
        }),
      ),
    { dispatch: false },
  );

  public readonly toggleManualProcessing$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.toggleManualProcessing),
      filter(() =>
        this._userSettingsSelectors.buyerConfigSettingsId().isSome(),
      ),
      switchMap(({ companyId, enabled }) => {
        const buyerConfigSettingsId = this._userSettingsSelectors
          .buyerConfigSettingsId()
          .unwrap();

        return this._surveyProcessingService
          .updateManualProcessing(buyerConfigSettingsId, companyId, enabled)
          .pipe(
            tap(() => {
              this._toastService.success(
                'Processing setting updated successfully',
              );
            }),
            tap(() => {
              this._userSettingsActions.updateBuyerSetting(
                'enableManualProcessing',
                companyId,
                enabled,
              );
            }),
            switchMap(() => {
              const logAction = SurveyActivityLogActions.logOperatorActivity({
                log: {
                  message: SURVEY_ACTIVITY_LOG_MESSAGES.manualProcessingToggled,
                  metadata: { action: enabled ? 'ON' : 'OFF' },
                },
              });

              return of(logAction);
            }),
            this.psCatchError(() => EMPTY, { notify: true }),
          );
      }),
    ),
  );

  private _userData(): { id: number; email: string; companyId: number } {
    const user = this._authSelectors.user().unwrap();
    return {
      id: user.id,
      email: user.email,
      companyId: this._authSelectors.activeCompanyId(),
    };
  }

  private _toastError(error: Nullable<string>): void {
    Option.from(error).inspect((message) => this._toastService.error(message));
  }

  private _notifyLocationUpload(
    result: SurveyLocationUploadResult,
    qualificationCode: number,
    qualificationName: string,
  ): void {
    if (!result.validCodes.length) {
      this._toastService.error(
        qualificationCode === QUALIFICATION_CODE.FSA
          ? LOCATION_UPLOAD_MESSAGES.INVALID_FSA_CODE_FILE
          : LOCATION_UPLOAD_MESSAGES.INVALID_LOCATION_CODE_FILE,
        { keepOpen: false },
      );
      return;
    }

    if (result.incorrectCodeCount) {
      this._toastService.warning(
        locationUploadRejectedMessage(
          result.incorrectCodeCount,
          result.incorrectCodes,
        ),
        { keepOpen: false },
      );
    }

    this._toastService.success(
      locationUploadSuccessMessage({
        correctCodeCount: result.correctCodeCount,
        totalCodeCount: result.totalCodeCount,
        duplicateCodeCount: result.duplicateCodeCount,
        modifiedCodeCount: result.modifiedCodeCount,
        qualificationName,
      }),
      { keepOpen: false },
    );
  }
}
